/** Contains helper functions for drag-and-drop container detection and reordering logic. */
// @ts-check
import { generateKeyBetween } from 'fractional-indexing';

/** Root level is `null`; a Firestore doc without the field hydrates as undefined. */
const normalizeParentId = (parentId) => parentId ?? null;

const resolveTargetParentId = (over, overData, allTasks, currentParentId) => {
  const { sortable, type: overType } = overData;

  if (['task-item', 'unscheduled-task', 'subtask'].includes(overType)) {
    const overTask = allTasks.find((t) => t.id === over.id);
    if (!overTask) return currentParentId;
    // Dropped on a hoofdtaak: move under it. Dropped on another subtask:
    // that is a reorder, so the parent stays put.
    return normalizeParentId(overTask.parentId)
      ? currentParentId
      : String(over.id);
  }

  // A containerId is only a parentId when it actually names a task —
  // `subtasks-of-<id>` and friends are not.
  const overContainer = sortable?.containerId;
  return allTasks.some((t) => t.id === overContainer)
    ? overContainer
    : currentParentId;
};

/**
 * Determines the parentId the dragged task should end up in after the drop.
 * Returns null for root level.
 *
 * Nesting rule: dropping a task on another task item is a reorder, never a
 * nest — a hoofdtaak dropped onto another hoofdtaak stays a hoofdtaak. The
 * exception is dropping directly onto a subtask panel, which is an explicit
 * request to nest under that panel's task. Promotion back to root goes
 * through the master-list drop zone.
 *
 * @param {DndActive} active
 * @param {DndOver | null} over
 * @param {Task[]} allTasks
 * @returns {string | null}
 */
export function resolveNewParentId(active, over, allTasks) {
  if (!over) return null;

  const overData = over.data?.current || {};

  // Dropping directly onto a subtask panel is an explicit nest request,
  // regardless of whether the dragged task is currently a hoofdtaak.
  if (overData.type === 'subtask-area') {
    return overData.parentTask?.id || null;
  }

  const activeTask = allTasks.find((t) => t.id === active.id);
  const currentParentId = normalizeParentId(activeTask?.parentId);
  if (!currentParentId) return null;

  // Only the empty background of the master list promotes to root. A drop
  // directly on a hoofdtaak row there has overData.type === 'unscheduled-task'
  // and must fall through to resolveTargetParentId below to reparent under it.
  if (over.id === 'master-list-drop-zone') {
    return null;
  }

  return resolveTargetParentId(over, overData, allTasks, currentParentId);
}

const applyInheritance = (payload, activeTask, newParent) => {
  if (!newParent) return;

  // Inherit status from new parent if task was root-level
  if (!activeTask.parentId && newParent.status) {
    payload.status = newParent.status;
  }

  // Inherit contexts from new parent if task has none
  if (newParent.contexts?.length > 0) {
    if (!activeTask.contexts || activeTask.contexts.length === 0) {
      payload.contexts = [...newParent.contexts];
    }
  }
};

/**
 * Builds the Firestore update payload for a completed drag-and-drop operation.
 * Handles parentId change, orderKey change, and status/contexts inheritance.
 * @param {Task} activeTask
 * @param {string | null} newParentId
 * @param {string} newOrderKey
 * @param {Task[]} allTasks
 * @returns {Partial<Task>}
 */
export function buildDropUpdatePayload(
  activeTask,
  newParentId,
  newOrderKey,
  allTasks,
) {
  const payload = {};

  if (newOrderKey !== activeTask.orderKey) {
    payload.orderKey = newOrderKey;
  }

  if (
    normalizeParentId(newParentId) !== normalizeParentId(activeTask.parentId)
  ) {
    payload.parentId = normalizeParentId(newParentId);
  }

  if (Object.keys(payload).length === 0) {
    return {};
  }

  if (newParentId && newParentId !== normalizeParentId(activeTask.parentId)) {
    const newParent = allTasks.find((t) => t.id === newParentId);
    if (newParent) {
      applyInheritance(payload, activeTask, newParent);
    }
  }

  return payload;
}

/**
 * Computes the orderKey when appending a task as the last subtask of newParentId.
 * @param {string | null} newParentId
 * @param {Task[]} allTasks
 * @returns {string}
 */
export function computeSubtaskOrderKey(newParentId, allTasks) {
  const siblings = allTasks.filter(
    (t) =>
      normalizeParentId(t.parentId) === normalizeParentId(newParentId) &&
      t.orderKey,
  );
  if (siblings.length === 0) {
    return generateKeyBetween(null, null);
  }
  const maxKey = siblings.reduce(
    (max, t) => (t.orderKey > max ? t.orderKey : max),
    '',
  );
  return generateKeyBetween(maxKey, null);
}

const getSiblingOrderKeys = (
  siblings,
  overIndex,
  isDraggingDown,
  overTaskKey,
) => {
  if (isDraggingDown) {
    const nextKey =
      overIndex < siblings.length - 1
        ? siblings[overIndex + 1]?.orderKey
        : null;
    return [overTaskKey, nextKey];
  }
  const prevKey = overIndex > 0 ? siblings[overIndex - 1]?.orderKey : null;
  return [prevKey, overTaskKey];
};

/**
 * Computes the orderKey when reordering within a sibling list.
 * @param {DndActive} active
 * @param {DndOver} over
 * @param {string | null} newParentId
 * @param {Task[]} allTasks
 * @returns {string}
 */
export function computeReorderOrderKey(active, over, newParentId, allTasks) {
  const siblings = allTasks
    .filter(
      (t) =>
        normalizeParentId(t.parentId) === normalizeParentId(newParentId) &&
        t.orderKey &&
        t.id !== active.id,
    )
    .sort((a, b) =>
      a.orderKey < b.orderKey ? -1 : a.orderKey > b.orderKey ? 1 : 0,
    );

  const overIndex = siblings.findIndex((t) => t.id === over.id);
  if (overIndex < 0) {
    const lastKey =
      siblings.length > 0 ? siblings[siblings.length - 1].orderKey : null;
    return generateKeyBetween(lastKey, null);
  }

  const activeTask = allTasks.find((t) => t.id === active.id);
  const overTask = siblings[overIndex];

  if (!activeTask?.orderKey || !overTask?.orderKey) {
    const prevKey = overIndex > 0 ? siblings[overIndex - 1]?.orderKey : null;
    return generateKeyBetween(prevKey, overTask?.orderKey);
  }

  const isDraggingDown = activeTask.orderKey < overTask.orderKey;
  const [lower, upper] = getSiblingOrderKeys(
    siblings,
    overIndex,
    isDraggingDown,
    overTask.orderKey,
  );
  return generateKeyBetween(lower, upper);
}

/**
 * Returns true if moving activeTask into newParentId would create a circular reference.
 * @param {Task} activeTask
 * @param {string | null} newParentId
 * @param {Task[]} allTasks
 * @returns {boolean}
 */
export function isCircularDrop(activeTask, newParentId, allTasks) {
  if (!newParentId) return false;
  if (newParentId === activeTask.id) return true;

  const newParentTask = allTasks.find((t) => t.id === newParentId);
  if (!newParentTask) return false;

  if (activeTask.materializedPath && newParentTask.materializedPath) {
    return newParentTask.materializedPath.startsWith(
      activeTask.materializedPath,
    );
  }

  return false;
}
